import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import api from "shared/api/client";
import "./UserPage.css";

interface User {
  _id: string;
  username: string;
  userrealname: string;
  role: "admin" | "employee";
}

type UserFormValues = {
  username: string;
  userrealname: string;
  password?: string;
  role: "admin" | "employee";
};

const getCurrentUserId = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return "";

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.id || payload._id || "";
  } catch {
    return "";
  }
};

const UserPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [form] = Form.useForm<UserFormValues>();

  const role = localStorage.getItem("userRole");
  const currentUserId = getCurrentUserId();
  const isAdmin = role === "admin";

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      const res = await api.get<User[]>("/auth");
      setUsers(res.data || []);
    } catch (err: any) {
      message.error(err.response?.data?.message || "Хэрэглэгч татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesRole = selectedRole ? user.role === selectedRole : true;
      const matchesSearch = query
        ? [user.username, user.userrealname, user.role]
            .map((value) => String(value || "").toLowerCase())
            .some((value) => value.includes(query))
        : true;

      return matchesRole && matchesSearch;
    });
  }, [search, selectedRole, users]);

  const stats = useMemo(
    () => ({
      total: users.length,
      admins: users.filter((user) => user.role === "admin").length,
      employees: users.filter((user) => user.role === "employee").length,
    }),
    [users]
  );

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "employee" });
    setIsModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      userrealname: user.userrealname,
      role: user.role,
      password: undefined,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        username: values.username.trim(),
        userrealname: values.userrealname.trim(),
        password: values.password?.trim(),
      };

      if (!payload.password) {
        delete payload.password;
      }

      if (editingUser) {
        await api.put(`/auth/${editingUser._id}`, payload);
        message.success("Хэрэглэгч шинэчлэгдлээ");
      } else {
        await api.post("/auth/register", payload);
        message.success("Хэрэглэгч нэмэгдлээ");
      }

      closeModal();
      fetchUsers();
    } catch (err: any) {
      if (!err.errorFields) {
        message.error(err.response?.data?.message || "Хадгалах үед алдаа гарлаа");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/auth/${id}`);
      message.success("Хэрэглэгч устгагдлаа");
      fetchUsers();
    } catch (err: any) {
      message.error(err.response?.data?.message || "Устгахад алдаа гарлаа");
    }
  };

  if (!isAdmin) {
    return (
      <Alert
        message="Зөвшөөрөл хүрэхгүй байна"
        description="User management хэсэг зөвхөн admin эрхтэй хэрэглэгчид харагдана."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className="user-page">
      <div className="user-header">
        <div>
          <h2>User Management</h2>
          <p>Ажилтан, admin эрх болон нэвтрэх хэрэглэгчдийг удирдана.</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          User нэмэх
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Нийт хэрэглэгч" value={stats.total} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Admin" value={stats.admins} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Employee" value={stats.employees} prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card className="user-filter-card">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Input.Search
              allowClear
              placeholder="Username, нэр, role хайх"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <Select
              allowClear
              placeholder="Role шүүх"
              value={selectedRole}
              onChange={setSelectedRole}
              options={[
                { label: "admin", value: "admin" },
                { label: "employee", value: "employee" },
              ]}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="user-centered-state">
          <Spin size="large" />
        </div>
      ) : (
        <Table<User>
          rowKey="_id"
          dataSource={filteredUsers}
          size="small"
          bordered
          pagination={{ pageSize: 50, showSizeChanger: true }}
          columns={[
            {
              title: "Username",
              dataIndex: "username",
              key: "username",
            },
            {
              title: "Нэр",
              dataIndex: "userrealname",
              key: "userrealname",
            },
            {
              title: "Role",
              dataIndex: "role",
              key: "role",
              render: (value: User["role"]) => (
                <Tag color={value === "admin" ? "red" : "cyan"}>{value}</Tag>
              ),
            },
            {
              title: "Үйлдэл",
              key: "actions",
              width: 140,
              render: (_, record) => {
                const isSelf = record._id === currentUserId;
                const canDelete = record.role !== "admin" && !isSelf;

                return (
                  <Space>
                    <Tooltip title="Засах">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Хэрэглэгч устгах уу?"
                      okText="Устгах"
                      cancelText="Болих"
                      disabled={!canDelete}
                      onConfirm={() => handleDelete(record._id)}
                    >
                      <Tooltip
                        title={
                          canDelete
                            ? "Устгах"
                            : "Admin эсвэл өөрийн хэрэглэгчийг устгахгүй"
                        }
                      >
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          disabled={!canDelete}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                );
              },
            },
          ]}
        />
      )}

      <Modal
        title={editingUser ? "User засах" : "User нэмэх"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={submitting}
        okText="Хадгалах"
        cancelText="Болих"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: "Username оруулна уу" },
              { min: 3, message: "Username хамгийн багадаа 3 тэмдэгт байна" },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="userrealname"
            label="Хэрэглэгчийн нэр"
            rules={[{ required: true, message: "Хэрэглэгчийн нэр оруулна уу" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "Шинэ password" : "Password"}
            rules={
              editingUser
                ? []
                : [{ required: true, message: "Password оруулна уу" }]
            }
            extra={editingUser ? "Хоосон үлдээвэл password өөрчлөгдөхгүй." : undefined}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select
              options={[
                { label: "employee", value: "employee" },
                { label: "admin", value: "admin" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserPage;
